'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';

import { Spinner } from './Spinner';
import { ApiClientError, createApiClient } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import { getInitData, hapticImpact } from '@/lib/tma';

interface Dispute {
  id: string;
  market_id: string;
  resolution_id: string;
  reason: string;
  evidence_url: string | null;
  status: 'open' | 'accepted' | 'rejected';
  review_notes: string | null;
  created_at: number;
  reviewed_at: number | null;
  question_ru: string;
  market_state: string;
  outcome: string;
}

export function MyDisputes({ refreshKey }: { refreshKey: number }) {
  const [open, setOpen] = useState(false);
  const [disputes, setDisputes] = useState<Dispute[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (disputes !== null) return;
    const api = createApiClient(getInitData);
    api
      .myDisputes()
      .then((r) => setDisputes(r.disputes))
      .catch((err: unknown) => {
        const e = err as ApiClientError;
        setError(e?.message ?? 'Ошибка');
      });
  }, [open, disputes]);

  useEffect(() => {
    setDisputes(null);
    setError(null);
  }, [refreshKey]);

  return (
    <div className="bg-bg-elev mb-4 border-l-[3px] border-line">
      <button
        onClick={() => {
          hapticImpact('light');
          setOpen((v) => !v);
        }}
        className="w-full p-4 flex items-center justify-between"
      >
        <span className="font-mono text-[10px] text-text-tertiary tracking-[0.15em]">
          ⚖ МОИ ОСПАРИВАНИЯ
        </span>
        <span className="text-accent display text-[18px]">{open ? '−' : '+'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4">
          {error && <p className="text-no text-[11px]">⚠️ {error}</p>}
          {!error && !disputes && (
            <div className="flex justify-center py-4">
              <Spinner size={18} />
            </div>
          )}
          {disputes && disputes.length === 0 && (
            <p className="font-mono text-[11px] text-text-tertiary text-center py-2 tracking-[0.05em] leading-relaxed">
              НЕТ ДИСПУТОВ.<br />
              <span className="opacity-70">Не согласен с резолюцией — открой тут</span>
            </p>
          )}
          {disputes && disputes.length > 0 && (
            <div className="space-y-2">
              {disputes.map((d) => (
                <Row key={d.id} d={d} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ d }: { d: Dispute }) {
  const statusBadge =
    d.status === 'open' ? (
      <span className="mono-tag bg-warn/20 text-warn px-2 py-0.5 text-[9px]">⏳ OPEN</span>
    ) : d.status === 'accepted' ? (
      <span className="mono-tag bg-yes/20 text-yes px-2 py-0.5 text-[9px]">✓ ACCEPTED</span>
    ) : (
      <span className="mono-tag bg-no/20 text-no px-2 py-0.5 text-[9px]">✕ REJECTED</span>
    );

  return (
    <Link
      href={`/market?id=${encodeURIComponent(d.market_id)}`}
      onClick={() => hapticImpact('light')}
      className="block bg-bg-elev2 p-3 transition-colors border-l-[2px] border-line"
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <span className="text-[13px] leading-snug truncate flex-1">{d.question_ru}</span>
        {statusBadge}
      </div>
      <p className="font-mono text-[10px] text-text-tertiary mb-1 tracking-[0.05em]">
        Резолюция: {d.outcome.toUpperCase()} · открыт {formatRelative(d.created_at)}
        {d.reviewed_at && <> · решение {formatRelative(d.reviewed_at)}</>}
      </p>
      <p className="text-[12px] text-text-secondary line-clamp-2">{d.reason}</p>
      {d.review_notes && d.status !== 'open' && (
        <p className="text-[11px] text-text-tertiary mt-1 italic">
          Комментарий оператора: {d.review_notes}
        </p>
      )}
    </Link>
  );
}
